import { useState } from "react";
import { api } from "@/lib/api";
import { DIMENSIONS, HEADERS, FOOTERS, LAYOUTS, PALETTE_ITEMS, FONT_ITEMS, currentItems, itemKey, patchFor, previewUrl } from "./catalog";

const LISTS = { header: HEADERS, footer: FOOTERS, layout: LAYOUTS, palette: PALETTE_ITEMS, font: FONT_ITEMS };
const NO_LOCKS = { header: false, footer: false, layout: false, palette: false, font: false };

/** Bias toward SOMETHING different than the previous pick so each re-roll feels fresh. */
function pickRandom(list, excludeKey) {
  if (list.length <= 1) return list[0] || null;
  const others = excludeKey != null ? list.filter((it) => itemKey(it) !== excludeKey) : list;
  const pool = others.length ? others : list;
  return pool[Math.floor(Math.random() * pool.length)];
}

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

/** Shuffle tab state: rolled combination, per-dimension locks (locked = keep the site's current value), apply-all. */
export function useShuffle(site, reload, { setBust, setFlash, busy, setBusy }) {
  const [shuffle, setShuffle] = useState(null);
  const [locks, setLocks] = useState(NO_LOCKS);

  function roll() {
    const prev = shuffle || {};
    const current = currentItems(site);
    const next = Object.fromEntries(DIMENSIONS.map((k) => [
      k, locks[k] ? current[k] : pickRandom(LISTS[k], prev[k] ? itemKey(prev[k]) : null),
    ]));
    setShuffle(next); setBust((b) => b + 1); setFlash(null);
  }

  const toggleLock = (kind) => setLocks((prev) => ({ ...prev, [kind]: !prev[kind] }));

  // Locked dims still send the SITE's current value so the iframe previews exactly what Apply All would do.
  function url(bust) {
    if (!shuffle) return `${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${site.slug}`;
    return previewUrl(site, {
      preview_header: shuffle.header.value, preview_footer: shuffle.footer.value,
      preview_template: shuffle.layout.template, preview_variant: shuffle.layout.variant,
      preview_palette: shuffle.palette.value, preview_font: shuffle.font.value,
    }, bust);
  }

  async function applyAll() {
    if (!shuffle || busy) return;
    const patch = Object.assign({}, ...DIMENSIONS.filter((k) => !locks[k]).map((k) => patchFor(k, shuffle[k])));
    if (!Object.keys(patch).length) { setFlash({ kind: "err", msg: "All 5 dimensions are locked — nothing to apply" }); return; }
    setBusy("shuffle:all"); setFlash(null);
    try {
      await api.patch(`/sites/${site.id}`, patch);
      setFlash({ kind: "ok", msg: `Applied shuffle: ${DIMENSIONS.map((k) => shuffle[k].label).join(" · ")}` });
      setShuffle(null);
      await reload?.();
    } catch (e) { setFlash({ kind: "err", msg: errMsg(e, "Failed to apply shuffle") }); }
    finally { setBusy(null); }
  }

  return { shuffle, locks, roll, toggleLock, url, applyAll };
}

/** Single-dimension apply for the tile grid. */
export function useApplyTile(site, reload, { setBust, setFlash, busy, setBusy }) {
  return async function apply(kind, item) {
    if (busy) return;
    setBusy(`${kind}:${itemKey(item)}`); setFlash(null);
    try {
      await api.patch(`/sites/${site.id}`, patchFor(kind, item));
      setFlash({ kind: "ok", msg: `Applied: ${item.label}` });
      setBust((b) => b + 1); // refresh iframes to reflect new "current"
      await reload?.();
    } catch (e) { setFlash({ kind: "err", msg: errMsg(e, "Failed to apply theme") }); }
    finally { setBusy(null); }
  };
}
